import {IconButton} from '@wandb/components';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Input, type InputOnChangeData} from 'semantic-ui-react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import {type RunHistoryKeyInfo} from '../../types/run';
import {DD_PRIVACY_ALLOW} from '../../util/datadog/privacy';
import {type RunsLinePlotConfig} from '../PanelRunsLinePlot/types';
import {
  getSelectedMetricCount,
  getTruncatedSelectedMetrics,
} from '../PanelRunsLinePlot/utils';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {
  type MetricsPickerProps,
  MetricsRegexToggle,
} from './MetricsPickerShared';
import {
  keepRegexActionsFromBlurringInput,
  RegexMetricPreview,
  type RegexMetricPreviewData,
} from './RegexMetricPreview';

const REGEX_METRIC_PREVIEW_DEBOUNCE_MS = 150;
const REGEX_METRIC_CONFIG_DEBOUNCE_MS = 750;

export const MetricsPickerRegexInput = (props: MetricsPickerProps) => {
  const {keyInfo, config, updateConfig} = props;
  const trackSettingPage = useTrackSettingPage();
  const latestProps = useRef({config, keyInfo, updateConfig});
  latestProps.current = {config, keyInfo, updateConfig};

  const [inputRegex, setInputRegex] = useState(config.metricRegex);
  const [metricPreview, setMetricPreview] = useState<RegexMetricPreviewData>(
    () => getRegexMetricPreview(config.metricRegex, keyInfo)
  );
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const onTogglePreviewExpanded = useCallback(() => {
    setIsPreviewExpanded(prev => !prev);
  }, []);

  // Keep the text input responsive. Metric matching scans every history key in
  // large workspaces, so doing it directly in onChange blocks keystrokes. The
  // shorter debounce updates only local preview state, while the longer config
  // debounce waits for a stronger idle signal before kicking off global chart
  // query/render work.
  const updatePreviewDebounced = useMemo(
    () =>
      debounce(
        (newRegex: string | undefined, newKeyInfo: RunHistoryKeyInfo) => {
          setMetricPreview(getRegexMetricPreview(newRegex, newKeyInfo));
        },
        REGEX_METRIC_PREVIEW_DEBOUNCE_MS
      ),
    []
  );

  const updateConfigDebounced = useMemo(
    () =>
      debounce((newRegex: string | undefined) => {
        const {
          config: latestConfig,
          keyInfo: latestKeyInfo,
          updateConfig: latestUpdateConfig,
        } = latestProps.current;
        const preview = getRegexMetricPreview(newRegex, latestKeyInfo);
        const newConfig: Partial<RunsLinePlotConfig> = {};

        if (newRegex !== latestConfig.metricRegex) {
          newConfig.metricRegex = newRegex;
        }

        // Avoid a no-op metrics write. Even if the regex text changed,
        // writing a value-identical metrics array still fans out through
        // Redux and rebuilds line data for the panel.
        if (!stringArraysEqual(preview.metrics, latestConfig.metrics)) {
          newConfig.metrics = preview.metrics;
        }

        if (Object.keys(newConfig).length > 0) {
          // intentionally omitting `useMetricRegex` since that gets populated
          // when user clicks on the regex button already
          latestUpdateConfig(newConfig);
        }
      }, REGEX_METRIC_CONFIG_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    return () => {
      updatePreviewDebounced.cancel();
      updateConfigDebounced.cancel();
    };
  }, [updateConfigDebounced, updatePreviewDebounced]);

  const onInputChanged = useCallback(
    (_: React.SyntheticEvent, {value: newRegex}: InputOnChangeData) => {
      setInputRegex(newRegex);
      setIsPreviewExpanded(newRegex.length > 0);
      updatePreviewDebounced(newRegex, keyInfo);
      updateConfigDebounced(newRegex);
    },
    [keyInfo, updateConfigDebounced, updatePreviewDebounced]
  );

  const onInputFocused = useCallback(() => {
    setIsPreviewExpanded(inputRegex != null && inputRegex.length > 0);
  }, [inputRegex]);

  const onInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
      }
    },
    []
  );

  const onInputBlurred = useCallback(() => {
    updatePreviewDebounced.flush();
    updateConfigDebounced.flush();
    setIsPreviewExpanded(false);
    trackSetting({
      action: 'change',
      page: trackSettingPage,
      settingLevel: 'panel',
      setting: 'yAxis',
      settingValue: inputRegex?.toString(),
      additionalProperties: {
        isInputRegexEmpty: !inputRegex?.length,
      },
    });
  }, [
    inputRegex,
    setIsPreviewExpanded,
    trackSettingPage,
    updateConfigDebounced,
    updatePreviewDebounced,
  ]);

  const onClearRegex = useCallback(() => {
    updatePreviewDebounced.cancel();
    updateConfigDebounced.cancel();
    setInputRegex('');
    setMetricPreview(getRegexMetricPreview(undefined, keyInfo));
    setIsPreviewExpanded(false);

    const newConfig: Partial<RunsLinePlotConfig> = {};
    if (config.metricRegex !== undefined) {
      newConfig.metricRegex = undefined;
    }
    if (!stringArraysEqual([], config.metrics)) {
      newConfig.metrics = [];
    }
    if (Object.keys(newConfig).length > 0) {
      updateConfig(newConfig);
    }
  }, [
    config.metricRegex,
    config.metrics,
    keyInfo,
    updateConfig,
    updateConfigDebounced,
    updatePreviewDebounced,
  ]);

  return (
    <div className="flex w-full min-w-0 flex-col gap-12">
      <div className="metrics-picker" data-dd-privacy={DD_PRIVACY_ALLOW}>
        <div className="metrics-picker-regex-input-wrapper">
          <Input
            aria-label="Metric regex"
            style={{flexGrow: 1}}
            className="regex-input"
            placeholder="Select metrics with a regular expression"
            value={inputRegex ?? ''}
            onChange={onInputChanged}
            onFocus={onInputFocused}
            onBlur={onInputBlurred}
            onKeyDown={onInputKeyDown}
          />
          <div
            className="metrics-picker-regex-actions"
            onMouseDown={keepRegexActionsFromBlurringInput}>
            <MetricsRegexToggle {...props} />
            <IconButton
              aria-label="Clear regex input"
              icon="close"
              onPress={onClearRegex}
              size="md"
              variant="ghost"
            />
          </div>
        </div>
      </div>
      {inputRegex != null && inputRegex.length > 0 && (
        <RegexMetricPreview
          preview={metricPreview}
          isExpanded={isPreviewExpanded}
          onToggleExpanded={onTogglePreviewExpanded}
        />
      )}
    </div>
  );
};

function getRegexMetricPreview(
  metricRegex: string | undefined,
  keyInfo: RunHistoryKeyInfo
): RegexMetricPreviewData {
  const args = {
    metricRegex,
    keyInfo,
    sortedKeys: keyInfo.sortedKeys,
  };
  return {
    count: getSelectedMetricCount(args),
    metrics: getTruncatedSelectedMetrics(args),
  };
}

// This sits in the regex-edit hot path. lodash.isEqual would be correct, but
// it pays for generic deep comparison; here we only need ordered string arrays
// with cheap reference/null/length bailouts.
function stringArraysEqual(
  left: string[] | undefined,
  right: string[] | undefined
) {
  if (left === right) {
    return true;
  }
  if (left == null || right == null || left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }
  return true;
}
